import {
  entitlementsPermission,
  parsePositiveIntEntitlement,
  type UserWithEntitlements,
} from "../../../utils/entitlements-permission";
import { BULK_POSTS_DEFAULT_LIMIT } from "../../../utils/plan-limits";

const DEFAULT_LIMIT = BULK_POSTS_DEFAULT_LIMIT;
const STORAGE_KEY = "bulk-create-post-limit";
export const CAROUSEL_POST_LIMIT = 10;

// Session-wide QA override. This is intentionally mutable so support/QA can
// adjust the limit mid-session from the console without a reload.
let runtimeLimit: number | null = null;

export const getBulkCreateLimit = (user?: UserWithEntitlements): number => {
  // Precedence: runtime > localStorage > entitlement > default
  if (runtimeLimit !== null) {
    return runtimeLimit;
  }

  const stored = parsePositiveIntEntitlement(localStorage.getItem(STORAGE_KEY));
  if (stored !== null) {
    return stored;
  }

  const entitlement = entitlementsPermission.getMaximumBulkPosts(user);
  if (entitlement !== null) {
    return entitlement;
  }

  return DEFAULT_LIMIT;
};

export const setBulkCreateLimit = (limit: number): void => {
  if (limit <= 0) {
    throw new Error("Limit must be greater than 0");
  }

  runtimeLimit = limit;
  localStorage.setItem(STORAGE_KEY, limit.toString());
};

export const resetBulkCreateLimit = (): void => {
  runtimeLimit = null;
  localStorage.removeItem(STORAGE_KEY);
};

// Global console function declarations
declare global {
  interface Window {
    setBulkCreateLimit: typeof setBulkCreateLimit;
    resetBulkCreateLimit: typeof resetBulkCreateLimit;
    getBulkCreateLimit: typeof getBulkCreateLimit;
  }
}

// Expose helpers on `window` so we can adjust the bulk-create limit from the
// browser console for QA / support repros. The server enforces the actual cap
// from the user's `maximum-bulk-posts` entitlement, so this override is
// cosmetic-only — it just changes how many forms the modal will open.
if (typeof window !== "undefined") {
  window.setBulkCreateLimit = setBulkCreateLimit;
  window.resetBulkCreateLimit = resetBulkCreateLimit;
  window.getBulkCreateLimit = getBulkCreateLimit;
}
